import { useState, useEffect } from 'react';
import { MapPin, X, Navigation } from 'lucide-react';

export default function LocationPrompt({ onGranted, onDismiss }) {
  const [status, setStatus] = useState('idle'); // idle | requesting | granted | denied

  useEffect(() => {
    // Check if already granted
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(perm => {
        if (perm.state === 'granted') {
          setStatus('granted');
          requestLocation();
        } else if (perm.state === 'denied') {
          setStatus('denied');
        }
      }).catch(() => {});
    }
  }, []);

  const requestLocation = () => {
    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStatus('granted');
        onGranted?.({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => { setStatus('denied'); },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  if (status === 'granted') return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[900] w-full max-w-sm px-4" data-testid="location-prompt">
      <div className="bg-[#2B2B2B] rounded-2xl p-5 shadow-2xl border border-white/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#C8A97E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Navigation size={18} className="text-[#C8A97E]" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-white text-sm">Allow location for nearby deals</p>
            <p className="text-white/55 text-xs mt-0.5 leading-relaxed">
              We&apos;ll show deals sorted by distance and verify in-store redemptions.
            </p>
          </div>
          <button onClick={onDismiss} className="text-white/30 hover:text-white ml-1">
            <X size={16} />
          </button>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={requestLocation}
            disabled={status === 'requesting'}
            data-testid="allow-location-btn"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#C8A97E] text-white text-xs font-bold hover:bg-[#b8956b] transition-colors disabled:opacity-60"
          >
            {status === 'requesting'
              ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Detecting...</>
              : <><MapPin size={13} /> Allow Location</>}
          </button>
          <button onClick={onDismiss} className="px-4 py-2.5 rounded-full bg-white/10 text-white/70 text-xs font-semibold hover:bg-white/20 transition-colors">
            Not now
          </button>
        </div>
        {status === 'denied' && (
          <p className="text-xs text-red-400 mt-3 text-center">Location access was blocked. Enable it in your browser settings.</p>
        )}
      </div>
    </div>
  );
}
